import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './Navbar.css';

const NAV_LINKS = [
  { id: 'home',     label: 'Home' },
  { id: 'about',    label: 'About' },
  { id: 'events',   label: 'Events' },
  { id: 'collaboration', label: 'Collaboration' },
  { id: 'contact',  label: 'Contact' },
  { id: 'join',     label: 'Join Us' },
];

export default function Navbar({ currentPage, navigate, onAuthClick, user, onSignOut }) {
  const [scrolled, setScrolled]       = useState(false);
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  /* Scroll detection */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Close user dropdown when clicking outside */
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target))
        setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* Lock body scroll when mobile menu open */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  /* Close on Escape */
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleNav = (page) => {
    navigate(page);
    setMobileOpen(false);
  };

  const initials = user?.user_metadata?.full_name
    ? user.user_metadata.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || 'U';

  /* Mobile menu rendered into document.body so nothing clips it */
  const mobileMenu = ReactDOM.createPortal(
    <div
      className={`mobile-menu-overlay ${mobileOpen ? 'mobile-menu-overlay--open' : ''}`}
      aria-hidden={!mobileOpen}
    >
      {/* Blurred backdrop — click to close */}
      <div className="mobile-menu-backdrop" onClick={() => setMobileOpen(false)} />

      {/* Drawer panel */}
      <div className="mobile-menu-panel" role="dialog" aria-modal="true" aria-label="Navigation menu">
        {/* Header */}
        <div className="mobile-menu-header">
          <button className="nav-logo" onClick={() => handleNav('home')} aria-label="Home">
            <img src="/echo-logo.png" alt="ECHO" className="mobile-menu-logo" />
          </button>
          <button
            className="mobile-menu-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        {/* Links */}
        <nav className="mobile-menu-links">
          {NAV_LINKS.map(link => (
            <button
              key={link.id}
              className={`mobile-nav-link ${currentPage === link.id ? 'mobile-nav-link--active' : ''}`}
              onClick={() => handleNav(link.id)}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Auth footer */}
        <div className="mobile-menu-footer">
          {user ? (
            <>
              <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginBottom: '0.8rem' }}>
                Signed in as <strong style={{ color: 'var(--ink)' }}>{user.user_metadata?.full_name || user.email}</strong>
              </p>
              <button
                className="mobile-auth-btn"
                style={{ background: 'var(--muted)' }}
                onClick={() => { setMobileOpen(false); onSignOut(); }}
              >
                Sign Out
              </button>
            </>
          ) : (
            <button
              className="mobile-auth-btn"
              onClick={() => { setMobileOpen(false); onAuthClick(); }}
            >
              Sign In / Sign Up
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
        {/* Logo */}
        <button className="nav-logo" onClick={() => handleNav('home')}>
          <img src="/echo-logo.png" alt="ECHO" className="nav-logo-img" />
        </button>

        {/* Desktop links */}
        <ul className="nav-links">
          {NAV_LINKS.map(link => (
            <li key={link.id}>
              <button
                className={`nav-link ${currentPage === link.id ? 'nav-link--active' : ''}`}
                onClick={() => handleNav(link.id)}
              >
                {link.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Right side */}
        <div className="nav-right">
          {user ? (
            <div className="user-menu-wrapper" ref={userMenuRef}>
              <button className="user-avatar-btn" onClick={() => setUserMenuOpen(v => !v)}>
                <span className="user-avatar">{initials}</span>
              </button>
              {userMenuOpen && (
                <div className="user-dropdown">
                  <div className="user-dropdown-header">
                    <div className="user-dropdown-name">{user.user_metadata?.full_name || 'Member'}</div>
                    <div className="user-dropdown-email">{user.email}</div>
                  </div>
                  <button
                    className="user-dropdown-item"
                    onClick={() => { setUserMenuOpen(false); onSignOut(); }}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="nav-signin-btn" onClick={onAuthClick}>Sign In</button>
          )}

          {/* Hamburger */}
          <button
            className="nav-hamburger"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={mobileOpen}
          >
            <span className="bar" />
            <span className="bar" />
            <span className="bar" />
          </button>
        </div>
      </nav>

      {/* Mobile menu portal */}
      {mobileMenu}
    </>
  );
}
